import React from "react";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { logError } from "@/lib/monitoring";

interface Props {
  children: React.ReactNode;
  /** Optional fallback UI — if omitted, a default card is shown */
  fallback?: React.ReactNode;
  /** When true, shows a compact inline card instead of full-screen */
  inline?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

const MAX_AUTO_RETRIES = 2;
const AUTO_RETRY_DELAY_MS = 1500;

/**
 * Granular ErrorBoundary with:
 * - Automatic retry (up to MAX_AUTO_RETRIES) before showing fallback UI
 * - Manual "retry component" (re-mount children without full page reload)
 * - React Query cache reset on recovery
 * - Full componentStack logging to monitoring backend
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const componentStack = info.componentStack ?? "unknown";

    logError("React ErrorBoundary caught", error, {
      componentStack: componentStack.slice(0, 2000),
      retryCount: this.state.retryCount,
      route: window.location.pathname,
      timestamp: new Date().toISOString(),
    });

    // Auto-retry: if we haven't exceeded the limit, schedule a re-mount
    if (this.state.retryCount < MAX_AUTO_RETRIES) {
      setTimeout(() => {
        this.setState((prev) => ({
          hasError: false,
          error: null,
          retryCount: prev.retryCount + 1,
        }));
      }, AUTO_RETRY_DELAY_MS);
    }
  }

  /** Manual retry — reset state to re-mount children */
  private handleRetry = () => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
  };

  /** Reset and also clear React Query caches via window event */
  private handleRetryWithQueryReset = () => {
    // Dispatch a custom event that QueryClientProvider consumers can listen to
    window.dispatchEvent(new CustomEvent("error-boundary-reset"));
    this.setState({ hasError: false, error: null, retryCount: 0 });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Custom fallback takes precedence
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isAutoRetrying = this.state.retryCount < MAX_AUTO_RETRIES;

    // Inline (compact) variant for section-level boundaries
    if (this.props.inline) {
      return (
        <Card className="border-destructive/30 my-4">
          <CardContent className="p-6 text-center space-y-3">
            <div className="mx-auto w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <p className="text-sm text-muted-foreground">
              {isAutoRetrying
                ? "Recuperando automaticamente…"
                : "Erro nesta seção. Tente recuperar abaixo."}
            </p>
            {!isAutoRetrying && (
              <div className="flex gap-2 justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={this.handleRetryWithQueryReset}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Tentar novamente
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // Full-screen fallback (root-level boundary)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              {isAutoRetrying ? "Recuperando…" : "Algo deu errado"}
            </h1>
            <p className="text-muted-foreground">
              {isAutoRetrying
                ? `Tentativa automática ${this.state.retryCount + 1} de ${MAX_AUTO_RETRIES}…`
                : "Ocorreu um erro inesperado. Tente recuperar ou recarregar a página."}
            </p>
          </div>

          {this.state.error && !isAutoRetrying && (
            <pre className="text-xs text-left bg-muted p-3 rounded-md overflow-auto max-h-32 text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}

          {!isAutoRetrying && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={this.handleRetryWithQueryReset}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" /> Recuperar componente
              </Button>
              <Button onClick={() => window.location.reload()} className="gap-2">
                <RefreshCw className="h-4 w-4" /> Recarregar página
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
